"use client"

import { use, useEffect, useState } from "react";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, Controller, FieldErrors, UseFormRegister, useWatch } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import LinkSelector from "../../LinkSelector";
import { Label } from "@/components/ui/label";
import SelectField from "../../input/SelectField";
import MultipleSelector, { Option } from "@/components/ui/MultipleSelector";
import { searchProducts } from "@/core/lib/api/account/sog/products";
import { getSelectedCategories, getSelectedProducts } from "@/core/lib/widget-params";
import { Switch } from "@/components/ui/switch";

interface ProductListWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

const LIST_OPTIONS = [
    { value: "select", label: "انتخابی" },
    { value: "new", label: "جدیدترین" },
    { value: "inventory", label: "موجود در انبار" },
];

export const ProductListWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: ProductListWidgetProps) => {
    const { dictionary } = useDictionary();

    const typeList = useWatch({
        control,
        name: `${name}.${widgetIndex}.type_list`
    })

    // دسته‌بندی‌ها فقط برای حالت «جدیدترین» لازمه (همون روش فرم تخفیف)
    const [categoryList, setCategoryList] = useState<Option[]>([]);

    useEffect(() => {
        if (typeList !== "new" || categoryList.length) return;

        fetch("/api/shop-categories")
            .then(res => res.json())
            .then(data => {
                if (!Array.isArray(data)) return;
                setCategoryList(data.map((item: Option) => ({ ...item, value: String(item.value) })));
            })
            .catch(console.error);
    }, [typeList, categoryList.length]);

    return (
        <div className="space-y-4">
            <MultilingualInput
                baseName={`${name}.${widgetIndex}.title`}
                label={dictionary.forms?.title}
                register={register}
                errors={errors}
                disabled={disabled}
            />
            <MultilingualInput
                baseName={`${name}.${widgetIndex}.subtitle`}
                label={dictionary.forms?.subtitle}
                register={register}
                errors={errors}
                disabled={disabled}
            />
            <MultilingualTextareaInput
                baseName={`${name}.${widgetIndex}.description`}
                label={dictionary.forms?.description}
                register={register}
                errors={errors}
                disabled={disabled}
            />
            <div className="space-y-1">
                <Label className="text-xs font-medium text-muted-foreground">
                    {dictionary.forms?.link}
                </Label>
                <Controller
                    control={control}
                    name={`${name}.${widgetIndex}.link`}
                    render={({ field: linkField }) => (
                        <LinkSelector
                            value={linkField.value || ''}
                            onChange={linkField.onChange}
                            placeholder={dictionary.forms?.link}
                        />
                    )}
                />
            </div>
            <div className="space-y-2">
                <Controller
                    control={control}
                    name={`${name}.${widgetIndex}.type_list`}
                    render={({ field, fieldState }) => (
                        <SelectField
                            label="مدل نمایش محصولات"
                            value={field.value || ""}
                            onValueChange={field.onChange}
                            options={LIST_OPTIONS}
                            placeholder="مدل نمایش محصولات"
                            disabled={disabled}
                            error={fieldState.error?.message}
                        />
                    )}
                />
            </div>

            {typeList === "new" &&
                <Controller
                    control={control}
                    name={`${name}.${widgetIndex}.params`}
                    render={({ field }) => {
                        // in "new" mode params is an object of filters; a value left over
                        // from "select" mode (an array) is dropped rather than spread
                        const filters = field.value && !Array.isArray(field.value) ? field.value : {};

                        return (
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-2">
                                    <label htmlFor={`is-available-${widgetIndex}`} className="text-primary-900">
                                        {`فقط محصولات ${dictionary.ui?.product?.available ?? "موجود در انبار"}`}
                                    </label>
                                    <Switch
                                        id={`is-available-${widgetIndex}`}
                                        checked={!!filters.is_available}
                                        onCheckedChange={checked =>
                                            field.onChange({ ...filters, is_available: checked })
                                        }
                                        disabled={disabled}
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label className="text-primary-900" htmlFor={field.name}>
                                        {dictionary.nav?.categories}
                                    </label>
                                    <MultipleSelector
                                        value={getSelectedCategories(field.value)}
                                        onChange={options =>
                                            field.onChange({ ...filters, selected_category_details: options })
                                        }
                                        placeholder={dictionary.nav?.categories}
                                        defaultOptions={categoryList}
                                        disabled={disabled}
                                        emptyIndicator={
                                            <p className="text-center text-sm leading-10 text-gray-600 dark:text-gray-400">
                                                {dictionary.common?.noResult}
                                            </p>
                                        }
                                    />
                                </div>
                            </div>
                        );
                    }}
                />
            }

            {typeList === "select" &&
                <Controller
                    control={control}
                    name={`${name}.${widgetIndex}.params`}
                    render={({ field }) => (
                        <div className="flex flex-col gap-2">
                            <label className="text-primary-900" htmlFor={field.name}>
                                {dictionary.nav?.products}
                            </label>

                            <MultipleSelector
                                value={getSelectedProducts(field.value)}
                                onChange={field.onChange}
                                placeholder={dictionary.nav?.products}
                                fetchFunction={searchProducts}
                                emptyIndicator={
                                    <p className="text-center text-sm leading-10 text-gray-600 dark:text-gray-400">
                                        {dictionary.common?.noResult}
                                    </p>
                                }
                            />
                        </div>
                    )}
                />

            }

        </div>
    );
};
